import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { format, parseISO, isToday, isTomorrow, addDays, isAfter, isBefore } from 'date-fns';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../contexts/AuthContext';

import api from '../../services/api';

// Function to get provider quotes from the API
const getProviderQuotes = async (providerId, status) => {
  try {
    let response;
    
    if (status && status !== 'all') {
      response = await api.getProviderQuotesByStatus(providerId, status);
    } else {
      response = await api.getProviderQuotes(providerId);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching provider quotes:', error);
    // Fallback to mock data if API fails
    const mockQuotes = [
    {
      id: '1',
      customer_id: 'cust1',
      customer_name: 'John Smith',
      customer_email: 'john.smith@example.com',
      customer_phone: '(555) 123-4567',
      job_type: 'painting',
      square_feet: 1200,
      address: '123 Main St, Anytown, CA 90210',
      amount: 2400,
      materials_cost: 800,
      labor_cost: 1600,
      description: 'Interior painting for living room, kitchen, and two bedrooms. Premium paint included.',
      status: 'pending',
      created_at: new Date(new Date().setDate(new Date().getDate() - 2)).toISOString(),
      pdf_url: 'https://example.com/quotes/1.pdf'
    },
    {
      id: '2',
      customer_id: 'cust2',
      customer_name: 'Sarah Johnson',
      customer_email: 'sarah.johnson@example.com',
      customer_phone: '(555) 987-6543',
      job_type: 'plumbing',
      square_feet: 0,
      address: '456 Oak Ave, Anytown, CA 90210',
      amount: 350,
      materials_cost: 150,
      labor_cost: 200,
      description: 'Fix leaky faucet in kitchen and replace bathroom sink drain.',
      status: 'accepted',
      created_at: new Date(new Date().setDate(new Date().getDate() - 5)).toISOString(),
      pdf_url: 'https://example.com/quotes/2.pdf'
    },
    {
      id: '3',
      customer_id: 'cust3',
      customer_name: 'Michael Brown',
      customer_email: 'michael.brown@example.com',
      customer_phone: '(555) 456-7890',
      job_type: 'electrical',
      square_feet: 0,
      address: '789 Pine St, Anytown, CA 90210',
      amount: 750,
      materials_cost: 300,
      labor_cost: 450,
      description: 'Install new light fixtures in living room and dining room. Upgrade electrical panel.',
      status: 'rejected',
      created_at: new Date(new Date().setDate(new Date().getDate() - 10)).toISOString(),
      pdf_url: 'https://example.com/quotes/3.pdf'
    },
    {
      id: '4',
      customer_id: 'cust4',
      customer_name: 'Emily Davis',
      customer_email: 'emily.davis@example.com',
      customer_phone: '(555) 789-0123',
      job_type: 'painting',
      square_feet: 2500,
      address: '101 Maple Dr, Anytown, CA 90210',
      amount: 4500,
      materials_cost: 1500,
      labor_cost: 3000,
      description: 'Exterior house painting, including prep work, primer, and two coats of paint.',
      status: 'pending',
      created_at: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
      pdf_url: 'https://example.com/quotes/4.pdf'
    },
    {
      id: '5',
      customer_id: 'cust5',
      customer_name: 'Robert Wilson',
      customer_email: 'robert.wilson@example.com',
      customer_phone: '(555) 234-5678',
      job_type: 'plumbing',
      square_feet: 0,
      address: '202 Cedar Ln, Anytown, CA 90210',
      amount: 1200,
      materials_cost: 400,
      labor_cost: 800,
      description: 'Replace water heater and install new shower fixtures in master bathroom.',
      status: 'accepted',
      created_at: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
      pdf_url: 'https://example.com/quotes/5.pdf'
    }
  ];
  
  // Filter by status if provided
  if (status && status !== 'all') {
    return mockQuotes.filter(quote => quote.status === status);
  }
  
  return mockQuotes;
  }
};

// This would be imported from your API service
const updateQuote = async (quoteId, updates) => {
  // Simulated API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  return true;
};

export default function QuotesPanel({ providerId }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editedQuote, setEditedQuote] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchQuotes();
  }, [tabValue]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      
      let status = 'all';
      switch (tabValue) {
        case 0: // All
          status = 'all';
          break;
        case 1: // Pending
          status = 'pending';
          break;
        case 2: // Accepted
          status = 'accepted';
          break;
        case 3: // Rejected
          status = 'rejected';
          break;
        default:
          status = 'all';
      }
      
      const data = await getProviderQuotes(
        providerId || currentUser.uid,
        status
      );
      
      setQuotes(data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load quotes. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type, quote) => {
    setDialogType(type);
    setSelectedQuote(quote);
    
    if (type === 'edit') {
      setEditedQuote({
        amount: quote.amount,
        materials_cost: quote.materials_cost,
        labor_cost: quote.labor_cost,
        description: quote.description
      });
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuote(null);
    setEditedQuote(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedQuote({
      ...editedQuote,
      [name]: name.includes('cost') || name === 'amount' ? parseFloat(value) : value
    });
  };

  const handleSaveQuote = async () => {
    try {
      setLoading(true);
      
      // Validate inputs
      if (editedQuote.amount <= 0 || 
          editedQuote.materials_cost < 0 || 
          editedQuote.labor_cost < 0 ||
          !editedQuote.description.trim()) {
        setSnackbar({
          open: true,
          message: 'Please enter valid quote details',
          severity: 'error'
        });
        return;
      }
      
      await updateQuote(selectedQuote.id, editedQuote);
      
      // Update local state
      const updatedQuotes = quotes.map(quote => 
        quote.id === selectedQuote.id 
          ? { ...quote, ...editedQuote } 
          : quote
      );
      
      setQuotes(updatedQuotes);
      
      setSnackbar({
        open: true,
        message: 'Quote updated successfully',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating quote:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update quote. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return 'Today';
    } else if (isTomorrow(date)) {
      return 'Tomorrow';
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />;
      case 'accepted':
        return <Chip label="Accepted" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<CancelIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Quotes
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="quote tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Quotes" />
          <Tab label="Pending" />
          <Tab label="Accepted" />
          <Tab label="Rejected" />
        </Tabs>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : quotes.length === 0 ? (
        <Alert severity="info">
          No quotes found for the selected criteria.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((quote) => (
                <TableRow key={quote.id}>
                  <TableCell>
                    {formatDate(quote.created_at)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {quote.customer_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quote.customer_phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {quote.job_type}
                    </Typography>
                    {quote.square_feet > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {quote.square_feet} sq ft
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ${quote.amount.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(quote.status)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleOpenDialog('view', quote)}
                        aria-label="view quote"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      {quote.status === 'pending' && (
                        <IconButton 
                          size="small" 
                          color="secondary" 
                          onClick={() => handleOpenDialog('edit', quote)}
                          aria-label="edit quote"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Dialogs */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {dialogType === 'view' && selectedQuote && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 1 }} />
                Quote Details
                <Box sx={{ ml: 'auto' }}>
                  {getStatusChip(selectedQuote.status)}
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Customer Information
                      </Typography>
                      <Typography variant="body1">
                        {selectedQuote.customer_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedQuote.customer_email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedQuote.customer_phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {selectedQuote.address}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Job Details
                      </Typography>
                      <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                        {selectedQuote.job_type}
                      </Typography>
                      {selectedQuote.square_feet > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Area: {selectedQuote.square_feet} sq ft
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Created: {format(new Date(selectedQuote.created_at), 'MMMM d, yyyy')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Quote Summary
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {selectedQuote.description}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Materials Cost:
                          </Typography>
                          <Typography variant="h6">
                            ${selectedQuote.materials_cost.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Labor Cost:
                          </Typography>
                          <Typography variant="h6">
                            ${selectedQuote.labor_cost.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Total Amount:
                          </Typography>
                          <Typography variant="h6" color="primary" fontWeight="bold">
                            ${selectedQuote.amount.toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                Close
              </Button>
              <Button 
                variant="outlined" 
                color="primary"
                startIcon={<ReceiptIcon />}
                href={selectedQuote.pdf_url}
                target="_blank"
              >
                View PDF
              </Button>
              {selectedQuote.status === 'pending' && (
                <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    handleCloseDialog();
                    handleOpenDialog('edit', selectedQuote);
                  }}
                >
                  Edit Quote
                </Button>
              )}
            </DialogActions>
          </>
        )}
        
        {dialogType === 'edit' && selectedQuote && editedQuote && (
          <>
            <DialogTitle>Edit Quote</DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    value={editedQuote.description}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={4}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Materials Cost ($)"
                    name="materials_cost"
                    type="number"
                    value={editedQuote.materials_cost}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Labor Cost ($)"
                    name="labor_cost"
                    type="number"
                    value={editedQuote.labor_cost}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Total Amount ($)"
                    name="amount"
                    type="number"
                    value={editedQuote.amount}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={loading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveQuote} 
                variant="contained" 
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
